import { OwnerEnvBadge } from './OwnerEnvBadge'
import { OwnerBreadcrumb } from './OwnerBreadcrumb'
import { OwnerIdentityChip } from './OwnerIdentityChip'
import { OwnerRecentsDropdown } from '../kit/OwnerCommandPalette'
import { type OwnerRoute } from '../ownerRouter'
import { useNls } from '../../../nls/nlsProvider'
import { formatFreshness } from './ownerFreshness'

export type OwnerHeaderDataState = 'idle' | 'loading' | 'live' | 'partial' | 'unavailable'

export const OwnerHeader = ({
  route,
  onRefresh,
  refreshLoading = false,
  onForceRefresh,
  refreshBusy = false,
  snapshotAt,
  dataState = 'idle',
  dataMessage,
  sourcesFailed,
  sourcesTotal,
  sessionRemainingMinutes,
  onOpenCommandPalette,
  onNavigate,
}: {
  route: OwnerRoute
  onRefresh?: () => void
  refreshLoading?: boolean
  onForceRefresh?: () => Promise<void>
  refreshBusy?: boolean
  snapshotAt?: Date | null
  dataState?: OwnerHeaderDataState
  dataMessage?: string
  sourcesFailed?: number
  sourcesTotal?: number
  sessionRemainingMinutes?: number | null
  onOpenCommandPalette?: () => void
  onNavigate?: (route: OwnerRoute) => void
}) => {
  const { t } = useNls()

  return (
    <header className="ownerHeader">
      <div className="ownerHeaderLeft">
        <OwnerEnvBadge />
        <OwnerBreadcrumb route={route} />
      </div>

      <div className="ownerHeaderRight">
        {/* Recents Dropdown */}
        <OwnerRecentsDropdown currentRoute={route} onNavigate={onNavigate} />

        {/* ⌘K Trigger Button */}
        {onOpenCommandPalette && (
          <button
            type="button"
            className="ownerCommandTrigger"
            onClick={onOpenCommandPalette}
            aria-label={t('NLSOwnerConsoleAriaCommandPalette')}
            title={t('NLSOwnerConsoleAriaCommandPalette')}
          >
            <span>{t('NLSOwnerConsoleSearch')}</span>
            <span className="ownerCommandTriggerKey">⌘K</span>
          </button>
        )}

        {/* Data Status Chip (Failure / Partial visibility) */}
        {dataState === 'loading' && (
          <span className="ownerStatusChip ownerStatusChip--loading">
            {t('NLSOwnerConsoleLoadingData')}
          </span>
        )}
        {dataState === 'partial' && (
          <span
            className="ownerStatusChip ownerStatusChip--warn"
            title={dataMessage || t('NLSOwnerConsoleSomeSourcesFailed')}
          >
            {sourcesFailed != null && sourcesTotal != null
              ? t('NLSOwnerConsoleSourcesFailed', { failed: sourcesFailed, total: sourcesTotal })
              : (dataMessage || t('NLSOwnerConsoleSourcesPartiallyFailed'))}
          </span>
        )}
        {dataState === 'unavailable' && (
          <span
            className="ownerStatusChip ownerStatusChip--danger"
            title={dataMessage || t('NLSOwnerConsoleAllSourcesFailed')}
          >
            {t('NLSOwnerConsoleDataUnavailable')}
          </span>
        )}

        {/* Session lease warning chip (< 5m remaining) */}
        {sessionRemainingMinutes != null && sessionRemainingMinutes < 5 && sessionRemainingMinutes >= 0 && (
          <span
            className="ownerStatusChip ownerStatusChip--warn"
            title={t('NLSOwnerConsoleSessionRemaining', { minutes: sessionRemainingMinutes })}
          >
            {t('NLSOwnerConsoleSessionRemaining', { minutes: sessionRemainingMinutes })}
          </span>
        )}

        {/* Freshness Chip next to Refresh */}
        {snapshotAt && (
          <span
            className="ownerFreshnessChip"
            title={formatFreshness(snapshotAt).tooltip}
          >
            {formatFreshness(snapshotAt).label}
          </span>
        )}

        {onRefresh && (
          <button
            type="button"
            className="ownerHeaderRefreshBtn"
            onClick={onRefresh}
            disabled={refreshLoading}
          >
            {refreshLoading ? t('NLSOwnerConsoleLoadingData') : t('NLSOwnerConsoleRefresh')}
          </button>
        )}

        <OwnerIdentityChip
          onForceRefresh={onForceRefresh}
          refreshBusy={refreshBusy}
        />
      </div>
    </header>
  )
}
